<template>
  <div>
    <v-dialog max-width="800" v-model="dialog">
      <template v-slot:activator="{ props: activatorPlaceholder }">
        <v-list-item v-bind="activatorPlaceholder" density="compact" link>
          <v-list-item-title
            style="font-size: 10pt"
            class="d-flex align-center"
          >
            <v-icon size="16" class="mr-2">mdi-bookshelf</v-icon>
            Start with a template
          </v-list-item-title>
        </v-list-item>
      </template>

      <template v-slot:default="{ isActive }">
        <v-card width="800">
          <v-sheet class="pa-4 bg-smokeDark">
            <h2>Category templates</h2>
          </v-sheet>

          <div class="bg-smokeDark">
            <v-text-field
              label="Search"
              prepend-inner-icon="mdi-magnify"
              variant="solo"
              flat
              hide-details=""
            ></v-text-field>
          </div>
          <v-divider></v-divider>
          <v-virtual-scroll :items="templates">
            <template v-slot="{ item }">
              <div class="pa-3 bg-smokeDark">
                <CategoryCard
                  :category="item"
                  type="template"
                  @click="selectTemplate(item)"
                />
              </div>
            </template>
          </v-virtual-scroll>
          <v-card-actions>
            <v-spacer></v-spacer>

            <v-btn text="Close Dialog" @click="isActive.value = false"></v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from "vuex";
import CategoryCard from "@/components/category/CategoryCard.vue";

export default {
  components: {
    CategoryCard,
  },
  computed: {
    ...mapState({
      suggested_categories: (state) =>
        state.posts.categories.filter((c) => c.status === "suggested"),
    }),
  },
  data() {
    return {
      dialog: false,
      templates: [
        {
          uid: "CAT_MYTH_REALITY",
          name: "Myth vs Reality",
          status: "draft",
          description:
            "Short posts that debunk a common industry myth and replace it with a practical, experience-based reality.",
          keywords: [
            "myth",
            "reality",
            "industry insights",
            "thought leadership",
          ],
          goals: [
            "Challenge common assumptions",
            "Position the brand as a credible expert",
            "Trigger engagement through contrast",
          ],
          last_post_generation: null,
          last_update: null,
          queue: [],
          schedule: {
            type: "weeks",
            frequency: 2,
            day_of_week: [2],
            timeslot: "09:00",
            month_day: null,
            last_slot: null,
          },
          posts: [],
        },
        {
          uid: "CAT_HOW_TO",
          name: "1-Minute How-To",
          status: "draft",
          description:
            "Actionable micro-guides that explain how to do one concrete thing in under a minute.",
          keywords: ["how to", "tips", "education", "practical"],
          goals: [
            "Deliver immediate value",
            "Build trust through usefulness",
            "Increase saves and shares",
          ],
          last_post_generation: null,
          last_update: null,
          queue: [],
          schedule: {
            type: "weeks",
            frequency: 2,
            day_of_week: [4],
            timeslot: "09:00",
            month_day: null,
            last_slot: null,
          },
          posts: [],
        },
        {
          uid: "CAT_MISTAKE_FIX",
          name: "Mistake → Fix",
          status: "draft",
          description:
            "Posts that highlight a common mistake and show the better alternative.",
          keywords: ["mistakes", "best practices", "learning"],
          goals: [
            "Educate through contrast",
            "Show expertise without preaching",
            "Encourage self-reflection",
          ],
          last_post_generation: null,
          last_update: null,
          queue: [],
          schedule: {
            type: "weeks",
            frequency: 2,
            day_of_week: [1],
            timeslot: "09:00",
            month_day: null,
            last_slot: null,
          },
          posts: [],
        },
        {
          uid: "CAT_CHECKLIST",
          name: "Checklist / Framework",
          status: "draft",
          description:
            "Reusable checklists or frameworks that people can save and apply.",
          keywords: ["framework", "checklist", "process"],
          goals: [
            "Increase saves",
            "Create evergreen content",
            "Demonstrate structured thinking",
          ],
          last_post_generation: null,
          last_update: null,
          queue: [],
          schedule: {
            type: "weeks",
            frequency: 1,
            day_of_week: [3],
            timeslot: "09:00",
            month_day: null,
            last_slot: null,
          },
          posts: [],
        },
        {
          uid: "CAT_BEFORE_AFTER",
          name: "Before / After",
          status: "draft",
          description:
            "Contrast posts showing a clear improvement over time or after an intervention.",
          keywords: ["before after", "transformation", "results"],
          goals: [
            "Make impact visible",
            "Tell outcome-driven stories",
            "Support credibility",
          ],
          last_post_generation: null,
          last_update: null,
          queue: [],
          schedule: {
            type: "weeks",
            frequency: 1,
            day_of_week: [4],
            timeslot: "09:00",
            month_day: null,
            last_slot: null,
          },
          posts: [],
        },
        {
          uid: "CAT_QUICK_WINS",
          name: "Quick Wins",
          status: "draft",
          description: "Small actions with disproportionate impact.",
          keywords: ["quick wins", "optimization", "tips"],
          goals: [
            "Deliver fast value",
            "Lower adoption barrier",
            "Increase engagement",
          ],
          last_post_generation: null,
          last_update: null,
          queue: [],
          schedule: {
            type: "weeks",
            frequency: 1,
            day_of_week: [5],
            timeslot: "09:00",
            month_day: null,
            last_slot: null,
          },
          posts: [],
        },
        {
          uid: "CAT_BEHIND_SCENES",
          name: "Behind the Scenes",
          status: "draft",
          description: "Show how the team works, thinks, or builds internally.",
          keywords: ["behind the scenes", "culture", "process"],
          goals: [
            "Humanize the brand",
            "Build transparency",
            "Strengthen employer brand",
          ],
          last_post_generation: null,
          last_update: null,
          queue: [],
          schedule: {
            type: "weeks",
            frequency: 1,
            day_of_week: [2],
            timeslot: "12:00",
            month_day: null,
            last_slot: null,
          },
          posts: [],
        },
        {
          uid: "CAT_TEAM_INTERVIEW",
          name: "Team Interview (1 Question)",
          status: "draft",
          description: "One team member answers one sharp question.",
          keywords: ["team", "interview", "people"],
          goals: [
            "Show expertise through people",
            "Build trust",
            "Strengthen team visibility",
          ],
          last_post_generation: null,
          last_update: null,
          queue: [],
          schedule: {
            type: "weeks",
            frequency: 1,
            day_of_week: [3],
            timeslot: "12:00",
            month_day: null,
            last_slot: null,
          },
          posts: [],
        },
        {
          uid: "CAT_TEAM_QUOTE",
          name: "Team Quote / Principle",
          status: "draft",
          description:
            "A quote or belief from a team member that reflects how the company thinks.",
          keywords: ["quotes", "principles", "culture"],
          goals: [
            "Express values",
            "Humanize expertise",
            "Create relatable content",
          ],
          last_post_generation: null,
          last_update: null,
          queue: [],
          schedule: {
            type: "weeks",
            frequency: 1,
            day_of_week: [1],
            timeslot: "12:00",
            month_day: null,
            last_slot: null,
          },
          posts: [],
        },
        {
          uid: "CAT_CLIENT_QUOTE",
          name: "Client Quote / Testimonial",
          status: "draft",
          description:
            "Client quotes with light context about the problem and outcome.",
          keywords: ["testimonial", "client", "trust"],
          goals: [
            "Build social proof",
            "Reduce buyer hesitation",
            "Increase credibility",
          ],
          last_post_generation: null,
          last_update: null,
          queue: [],
          schedule: {
            type: "weeks",
            frequency: 1,
            day_of_week: [4],
            timeslot: "12:00",
            month_day: null,
            last_slot: null,
          },
          posts: [],
        },
        {
          uid: "CAT_CUSTOMER_WIN",
          name: "Customer Win Snapshot",
          status: "draft",
          description:
            "Short result-first stories highlighting customer impact.",
          keywords: ["case", "results", "success"],
          goals: [
            "Show outcomes",
            "Support sales enablement",
            "Inspire prospects",
          ],
          last_post_generation: null,
          last_update: null,
          queue: [],
          schedule: {
            type: "weeks",
            frequency: 1,
            day_of_week: [5],
            timeslot: "12:00",
            month_day: null,
            last_slot: null,
          },
          posts: [],
        },
        {
          uid: "CAT_FEATURE_SPOTLIGHT",
          name: "Feature / Capability Spotlight",
          status: "draft",
          description:
            "Explain one feature, capability, or service and why it matters.",
          keywords: ["product", "feature", "value"],
          goals: [
            "Educate users",
            "Increase adoption",
            "Support product marketing",
          ],
          last_post_generation: null,
          last_update: null,
          queue: [],
          schedule: {
            type: "weeks",
            frequency: 1,
            day_of_week: [2],
            timeslot: "15:00",
            month_day: null,
            last_slot: null,
          },
          posts: [],
        },
        {
          uid: "CAT_FAQ",
          name: "FAQ / Objection Handler",
          status: "draft",
          description:
            "Answer one recurring customer question or objection per post.",
          keywords: ["faq", "objections", "sales enablement"],
          goals: [
            "Reduce friction",
            "Support decision-making",
            "Clarify value",
          ],
          last_post_generation: null,
          last_update: null,
          queue: [],
          schedule: {
            type: "weeks",
            frequency: 1,
            day_of_week: [3],
            timeslot: "15:00",
            month_day: null,
            last_slot: null,
          },
          posts: [],
        },
        {
          uid: "CAT_PARTNER_SPOTLIGHT",
          name: "Partner Spotlight",
          status: "draft",
          description: "Highlight a partner and a shared project or lesson.",
          keywords: ["partners", "collaboration", "ecosystem"],
          goals: [
            "Strengthen relationships",
            "Expand reach",
            "Show ecosystem value",
          ],
          last_post_generation: null,
          last_update: null,
          queue: [],
          schedule: {
            type: "weeks",
            frequency: 1,
            day_of_week: [1],
            timeslot: "15:00",
            month_day: null,
            last_slot: null,
          },
          posts: [],
        },
      ],
    };
  },
  methods: {
    selectTemplate(template) {
      console.log(template);
      this.$emit("selectTemplate", template);
      this.dialog = false;
    },
  },
};
</script>
